import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Award, Building2, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { certificationCategories, certifications } from '../data/certifications';
import { useLocale } from '../context/LocaleContext';

const FILTER_ALL = 'all';

const CertificationPreviewCard = ({ certification, index, categoryLabel, description }) => {
  const indexLabel = String(index + 1).padStart(2, '0');

  return (
    <article className="certifications-section__preview-card">
      <div className="certifications-section__preview-media">
        <img src={certification.image} alt={certification.title} loading="eager" />
      </div>

      <div className="certifications-section__preview-body">
        <div className="certifications-section__preview-meta">
          <span className="certifications-section__preview-index">{indexLabel}</span>
          <span className="rihen-tag rihen-tag--gold">{categoryLabel}</span>
        </div>

        <header className="certifications-section__preview-head">
          <Award size={17} className="certifications-section__preview-icon" aria-hidden="true" />
          <h3 className="certifications-section__preview-title">{certification.title}</h3>
        </header>

        <p className="certifications-section__preview-desc">{description}</p>

        <ul className="certifications-section__preview-details">
          <li>
            <Building2 size={14} aria-hidden="true" />
            <span>{certification.issuer}</span>
          </li>
          <li>
            <Calendar size={14} aria-hidden="true" />
            <span>{certification.year}</span>
          </li>
        </ul>

        <div className="certifications-section__preview-topics">
          {certification.topics.map((topic) => (
            <span key={topic} className="certifications-section__preview-topic">
              {topic}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
};

const CertificationsCarousel = ({
  showIntroHeader = true,
  introEyebrow,
  introTitle,
  introTitleLine2,
  introTitleClassName = 'hero-manifest__title hero-manifest__title--on-surface',
  introSubtitle,
}) => {
  const { lang: locale, t } = useLocale();
  const reduceMotion = useReducedMotion();
  const thumbsRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const filters = useMemo(
    () => [
      { id: FILTER_ALL, label: t('certifications_filter_all') },
      ...Object.entries(certificationCategories).map(([id, labels]) => ({
        id,
        label: labels[locale] ?? labels.fr,
      })),
    ],
    [locale, t],
  );

  const visibleCerts = useMemo(
    () =>
      activeFilter === FILTER_ALL
        ? certifications
        : certifications.filter((cert) => cert.category === activeFilter),
    [activeFilter],
  );

  const total = visibleCerts.length;
  const activeCert = visibleCerts[activeIndex] ?? visibleCerts[0];

  const getCategoryLabel = useCallback(
    (categoryId) =>
      certificationCategories[categoryId]?.[locale] ?? certificationCategories[categoryId]?.fr,
    [locale],
  );

  const getDescription = useCallback(
    (certification) =>
      locale === 'en' && certification.descriptionEn
        ? certification.descriptionEn
        : certification.description,
    [locale],
  );

  useEffect(() => {
    setActiveIndex(0);
    setDirection(0);
  }, [activeFilter]);

  useEffect(() => {
    const rail = thumbsRef.current;
    if (!rail) return;
    const activeThumb = rail.querySelector('.cert-carousel__thumb.is-active');
    if (!activeThumb) return;

    const targetLeft =
      activeThumb.offsetLeft - (rail.clientWidth - activeThumb.offsetWidth) / 2;

    rail.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: reduceMotion ? 'instant' : 'smooth',
    });
  }, [activeIndex, activeFilter, reduceMotion]);

  const goTo = useCallback(
    (index) => {
      const next = ((index % total) + total) % total;
      setActiveIndex((prev) => {
        if (next === prev) return prev;
        const forward = (next - prev + total) % total;
        const backward = (prev - next + total) % total;
        setDirection(forward <= backward ? 1 : -1);
        return next;
      });
    },
    [total],
  );

  const goNext = useCallback(() => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  if (!activeCert) return null;

  const counter = `${String(activeIndex + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;

  const previewVariants = {
    enter: (slideDirection) => ({
      opacity: 0,
      x: slideDirection > 0 ? 20 : -20,
    }),
    center: {
      opacity: 1,
      x: 0,
    },
    exit: (slideDirection) => ({
      opacity: 0,
      x: slideDirection > 0 ? -20 : 20,
    }),
  };

  return (
    <div className="certifications-section__layout">
      <div className="certifications-section__nav">
        {showIntroHeader ? (
          <SectionHeader
            eyebrow={introEyebrow}
            title={introTitle}
            titleLine2={introTitleLine2}
            titleClassName={introTitleClassName}
            subtitle={introSubtitle}
            centered={false}
            className="certifications-section__head"
          />
        ) : null}

        <div
          className="cert-carousel__filters"
          role="tablist"
          aria-label={t('certifications_filter_label')}
          data-reveal
        >
          {filters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              role="tab"
              aria-selected={activeFilter === filter.id}
              className={`cert-carousel__filter${activeFilter === filter.id ? ' is-active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div
          className="cert-carousel__thumbs"
          ref={thumbsRef}
          role="tablist"
          aria-label={t('certifications_carousel_label')}
          data-reveal
        >
          {visibleCerts.map((certification, index) => (
            <button
              key={certification.id}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={certification.title}
              className={`cert-carousel__thumb${index === activeIndex ? ' is-active' : ''}`}
              onClick={() => goTo(index)}
            >
              <img src={certification.image} alt="" loading="lazy" />
            </button>
          ))}
        </div>

        <div className="certifications-section__controls">
          <button
            type="button"
            className="cert-carousel__arrow"
            onClick={goPrev}
            aria-label={t('certifications_prev_aria')}
          >
            <ChevronLeft size={22} />
          </button>

          <div className="cert-carousel__counter" aria-live="polite">
            <span className="cert-carousel__counter-current">
              {String(activeIndex + 1).padStart(2, '0')}
            </span>
            <span className="cert-carousel__counter-sep">/</span>
            <span className="cert-carousel__counter-total">{String(total).padStart(2, '0')}</span>
          </div>

          <button
            type="button"
            className="cert-carousel__arrow"
            onClick={goNext}
            aria-label={t('certifications_next_aria')}
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <p className="cert-carousel__hint">{t('certifications_carousel_hint')}</p>
      </div>

      <div className="certifications-section__showcase" aria-live="polite" data-reveal="right">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={`${activeFilter}-${activeCert.id}`}
            className="certifications-section__showcase-panel"
            custom={direction}
            variants={reduceMotion ? undefined : previewVariants}
            initial={reduceMotion ? false : 'enter'}
            animate="center"
            exit={reduceMotion ? undefined : 'exit'}
            transition={{ duration: 0.28, ease: [0.16, 0.84, 0.3, 1] }}
          >
            <CertificationPreviewCard
              certification={activeCert}
              index={activeIndex}
              categoryLabel={getCategoryLabel(activeCert.category)}
              description={getDescription(activeCert)}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="sr-only" aria-live="polite">
        {counter} — {activeCert.title}
      </p>
    </div>
  );
};

export default CertificationsCarousel;
